import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PreferenceRecordInput, PreferenceRecord} from "./interfaces";
import { useArgoContext } from "../../ArgoContext";
import { ArgoPreferenceClient } from "./ArgoPreferenceClient";
import { get as _get } from "lodash";
import { getArgoConfig } from "../../window";

function getApplicationName(config: any): string {
    return _get(config, 'argo.applicationName', 'Argo');
}

/**
 * A hook that provides localStorage-like functionality using Argo Preferences service.
 * Similar to useLocalStorage, it returns [value, setValue] tuple with loading and error states.
 * @param key - The preference key to store/retrieve data
 * @param initialValue - Default value to use if no preference exists (can be null/undefined)
 * @returns Tuple of [value, setValue, loading, error]
 */
export function useArgoPrefs<T>(
    key: string,
    initialValue: T | null = null
): [T | null, (value: T | null) => Promise<void>, boolean, Error | null] {
    const context = useArgoContext();
    const [storedValue, setStoredValue] = useState<T | null>(initialValue);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    const initialValueRef = useRef(initialValue);
    const stableInitialValue = initialValueRef.current;

    const fetch = context.fetchWithAwsAuth;
    const client = useMemo(() => {
        return new ArgoPreferenceClient(fetch);
    }, [fetch]);

    const applicationName = useMemo(() => {
        return getApplicationName(getArgoConfig() || {});
    }, []);

    const getPreference = useCallback(async (): Promise<void> => {
        if (!key) return;
        setLoading(true);
        try {
            const getUserPreferenceInput: PreferenceRecordInput = {
                applicationName,
                key
            };

            const response = await client.batchGetUserPreference([getUserPreferenceInput]);

            if (response.preferenceList.length === 0) {
                setStoredValue(stableInitialValue);
                setError(null);
            } else {
                const preferenceData = response.preferenceList[0].preferenceData;
                try {
                    const parsedValue = JSON.parse(preferenceData);
                    setStoredValue(parsedValue);
                    setError(null);
                } catch (parseError) {
                    setStoredValue(preferenceData as T);
                    setError(null);
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err : new Error(`Failed to get preference with cause ${err}`));
            setStoredValue(stableInitialValue);
        } finally {
            setLoading(false);
        }
    }, [key, applicationName, stableInitialValue, client]);

    const setValue = useCallback(async (value: T | null): Promise<void> => {
        if (!key) return;

        setLoading(true);
        try {
            if (value === null || value === undefined) {
                const preferenceInput: PreferenceRecord = {
                    applicationName,
                    key,
                    preferenceData: JSON.stringify("")
                };
                await client.batchSaveUserPreference([preferenceInput]);
            } else {
                const preferenceData = typeof value === 'string' ? value : JSON.stringify(value);

                const preferenceInput: PreferenceRecord = {
                    applicationName,
                    key,
                    preferenceData
                };
                await client.batchSaveUserPreference([preferenceInput]);
            }
            setStoredValue(value);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(`Failed to save preference with cause ${err}`));
        } finally {
            setLoading(false);
        }
    }, [key, applicationName, client]);

    useEffect(() => {
        getPreference();
    }, [getPreference]);

    return [storedValue, setValue, loading, error];
}